import { createElement } from 'react'
import type React from 'react'
import type { ComponentPropsWithoutRef, ElementType } from 'react'
import { createVariants } from 'shared-ui/lib'
import { isNil, isNonNil } from 'utils'

import type { SpriteMap, SpriteMap16, SpriteMapCustom, SpriteMapPredefined } from '../global'
import { CircleSpinner } from './CircleSpinner'
import { type DisabledGuard, DisabledReason, findActiveGuard, swallowClick } from './DisabledGuard'
import { Icon, type IconRotation } from './Icon'

export enum ButtonSize {
  Default = 'default',
  Small = 'small'
}

export enum ButtonKind {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  Danger = 'danger',
  White = 'white',
  Accent = 'accent'
}

// Inactive buttons carry `aria-disabled` rather than the native `disabled` attribute. A natively
// disabled element receives no pointer or focus events, so nothing could open the reason tooltip;
// wrapping the button to catch those events instead would break callers' layout classes.
const baseButtonClasses = createVariants({
  base: 'flex text-gray-900 items-center gap-2 px-4 py-2 place-content-center pan-body-bold whitespace-nowrap aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
  variants: {
    kind: {
      primary:
        'text-white bg-green-500 hover:bg-green-600 active:bg-green-700 aria-disabled:hover:bg-green-500 aria-disabled:active:bg-green-500',
      secondary:
        'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 aria-disabled:hover:bg-gray-100 aria-disabled:active:bg-gray-100',
      tertiary:
        'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 aria-disabled:hover:bg-gray-200 aria-disabled:active:bg-gray-200',
      danger:
        'bg-ruby-100 hover:bg-ruby-200 active:bg-ruby-300 aria-disabled:hover:bg-ruby-100 aria-disabled:active:bg-ruby-100',
      white:
        'bg-white hover:bg-gray-100 active:bg-gray-200 aria-disabled:hover:bg-white aria-disabled:active:bg-white',
      accent:
        'bg-accent-100 hover:bg-accent-200 active:bg-accent-300 aria-disabled:hover:bg-accent-100 aria-disabled:active:bg-accent-100'
    },
    size: {
      default: 'h-10 rounded-lg',
      small: 'h-8 rounded'
    }
  },
  defaultVariants: {
    kind: ButtonKind.Primary,
    size: ButtonSize.Default
  }
})

const iconClasses = createVariants({ base: 'size-4 shrink-0' })

// Presentation props owned by the button, independent of which element it renders as.
type PanoramaButtonOwnProps = {
  children?: React.ReactNode
  className?: string
  kind?: ButtonKind
  size?: ButtonSize
  icon?: SpriteMap | SpriteMap16 | SpriteMapCustom | SpriteMapPredefined
  iconLeft?: true
  iconRotation?: IconRotation
  iconSize?: `size-${string}`
  /**
   * The button is disabled while any guard holds, and the first holding guard's reason is shown
   * when the button is hovered or focused. This replaces a bare `disabled`, so that a disabled
   * button always says why.
   */
  disabledWhen?: DisabledGuard | ReadonlyArray<DisabledGuard>
  /** Not accepted, including through a spread: use `disabledWhen`, which carries the reason. */
  disabled?: never
  /** Replaces the content with a spinner and blocks clicks until the work finishes. */
  loading?: boolean
  // Historically the button accepted a bare `() => void` alongside a real mouse handler; kept so
  // callers passing a plain callback still type-check.
  onClick?: (() => void) | React.MouseEventHandler
}

// Polymorphic: `as` defaults to `button`, but callers may render the button as a router `Link`,
// a `label` (file-upload inputs), an `a` (downloads), etc. Props for the chosen element are fully
// type-checked — `to` is required for `Link`, `htmlFor` for `label`, `href` for `a`, and so on.
export type PanoramaButtonProps<T extends ElementType = 'button'> = PanoramaButtonOwnProps & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof PanoramaButtonOwnProps | 'as' | 'aria-disabled'>

export const PanoramaButton = <T extends ElementType = 'button'>({
  as,
  kind,
  size,
  className,
  icon,
  iconLeft,
  iconRotation,
  iconSize,
  disabledWhen,
  loading,
  onClick,
  children,
  ...rest
}: PanoramaButtonProps<T>) => {
  const activeGuard = findActiveGuard(disabledWhen)
  const isInactive = activeGuard !== undefined || loading === true

  const content =
    loading === true ? (
      <CircleSpinner size="size-4" />
    ) : (
      <>
        {iconLeft && isNonNil(icon) && (
          <Icon
            icon={icon}
            rotation={iconRotation}
            className={iconClasses({ className: iconSize })}
          />
        )}
        {children}
        {isNil(iconLeft) && isNonNil(icon) && (
          <Icon
            icon={icon}
            rotation={iconRotation}
            className={iconClasses({ className: iconSize })}
          />
        )}
      </>
    )

  const element = createElement(
    as ?? 'button',
    {
      ...rest,
      'aria-disabled': isInactive ? true : undefined,
      onClick: isInactive ? swallowClick : onClick,
      className: baseButtonClasses({ kind, size, className })
    },
    content
  )

  return <DisabledReason reason={activeGuard?.reason}>{element}</DisabledReason>
}

PanoramaButton.displayName = 'PanoramaButton'
